import {
  bigint,
  index,
  integer,
  pgTable,
  text,
  unique,
} from 'drizzle-orm/pg-core'
import { relations, sql } from 'drizzle-orm'
import {
  getBaseFields,
  getBaseIndexes,
  getNullableIdReference,
  getTimestampWithTimezone,
} from '../common/utils'
import { newsletters } from './newsletter'
import { users } from './user'

export const shortUrls = pgTable(
  'short_urls',
  {
    ...getBaseFields(),
    key: text().notNull(),
    targetUrl: text().notNull(),
    openedAt: getTimestampWithTimezone(),
    expiresAt: getTimestampWithTimezone(),
    newsletterId: getNullableIdReference(() => newsletters.id),
    userId: getNullableIdReference(() => users.id),
    clickCount: integer().notNull().default(0),
    purpose: text(),
    /**
     * Set when the key is not just a redirect but the credential for one
     * scoped action — "answer this visit's questionnaire", never "log in".
     * NULL for ordinary delivery links, which is most rows. The scope decides
     * which table `subjectId` points at; see modules/shortUrl/scopedAccess.ts.
     */
    scope: text(),
    subjectId: bigint({ mode: 'number' }),
    /**
     * Kill switch for a key that leaked before its expiry. Honoured by
     * `liveShortUrl()`, so setting it by hand stops the link redirecting and
     * opening at once, and the next mint issues a fresh key. No code path
     * sets it — nothing in the product needs to revoke on its own yet.
     */
    revokedAt: getTimestampWithTimezone(),
  },
  (table) => [
    ...getBaseIndexes('short_urls', table),
    unique('short_urls_key_unique').on(table.key),
    // "Does this entity already have a live link?" — the lookup every mint
    // does before issuing a second key for the same thing.
    // `scope IS NOT NULL` keeps ordinary delivery links out of the index —
    // they are the bulk of the table (a newsletter wave inserts thousands),
    // and every one of them would otherwise carry an all-NULL entry no query
    // can read.
    index('short_urls_scope_subject_idx')
      .on(table.scope, table.subjectId)
      .where(
        sql`${table.scope} IS NOT NULL AND ${table.revokedAt} IS NULL AND ${table.deletedAt} IS NULL`,
      ),
    index('short_urls_newsletter_id_idx').on(table.newsletterId),
    index('short_urls_user_id_idx').on(table.userId),
  ],
)

export const shortUrlsRelations = relations(shortUrls, ({ one }) => ({
  newsletter: one(newsletters, {
    fields: [shortUrls.newsletterId],
    references: [newsletters.id],
  }),
  user: one(users, {
    fields: [shortUrls.userId],
    references: [users.id],
  }),
}))
